import {
  GetNotesForClaimQuery,
  NoteFilterInput,
  NoteTargetAreaType,
  PaginationSort,
} from '../gql/graphql';

import { getPaginatedNodes } from './utils';
import { graphql } from '../gql';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';

export type NoteNode = GetNotesForClaimQuery['notes']['edges'][0]['node'];

const PAGE_SIZE = 10;

export const GET_NOTES_FOR_CLAIM = graphql(`
  query getNotesForClaim(
    $filters: NoteFilterInput!
    $pagination: PaginationRequest
    $sort: PaginationSort
  ) {
    notes(filters: $filters, pagination: $pagination, sort: $sort) {
      pageInfo {
        totalResults
        hasNextPage
        hasPreviousPage
      }
      edges {
        cursor
        node {
          uuid
          targetUuid
          targetType
          targetName
          categories
          editable
          note
          attachments {
            edges {
              node {
                uuid
                targetType
                targetName
                targetUuid
                dataSource
                filePath
                folder
                createdAt
                updatedAt
                url
              }
            }
          }
          label
          createdAt
          updatedAt
          user {
            node {
              uuid
              username
              firstName
              lastName
            }
          }
        }
      }
    }
  }
`);

export interface GetNoteForClaimParams {
  claimUuid: string;
  sort?: PaginationSort;
  additionalFilters?: Partial<NoteFilterInput>;
  skip?: boolean;
}

export function useGetNoteForClaim({
  claimUuid,
  sort,
  additionalFilters = {},
  skip = false,
}: GetNoteForClaimParams) {
  // lastCursor is the numeric cursor of the last loaded item; null = first page
  const [lastCursor, setLastCursor] = useState<number | null>(null);
  const [allNotes, setAllNotes] = useState<NoteNode[]>([]);
  const [hasNextPage, setHasNextPage] = useState(false);

  const filters: NoteFilterInput = {
    targetUuid: claimUuid,
    targetArea: NoteTargetAreaType.Claim,
    ...additionalFilters,
  };

  const queryKey = JSON.stringify({ filters, sort });

  // Reset when filters or sort change
  useEffect(() => {
    setLastCursor(null);
    setAllNotes([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryKey]);

  const {
    loading,
    error,
    data,
    refetch: apolloRefetch,
  } = useQuery(GET_NOTES_FOR_CLAIM, {
    variables: {
      filters,
      // First page: no after (server default sort = newest first)
      // Subsequent pages: after = last cursor (continues in same sort order)
      pagination:
        lastCursor !== null
          ? { first: PAGE_SIZE, after: lastCursor }
          : { first: PAGE_SIZE },
      ...(sort ? { sort } : {}),
    },
    fetchPolicy: 'network-only',
    skip: !claimUuid || skip,
  });

  useEffect(() => {
    if (!data) return;
    const newNotes = getPaginatedNodes<NoteNode>(data.notes);
    if (lastCursor === null) {
      setAllNotes(newNotes);
    } else {
      setAllNotes((prev) => {
        const existingUuids = new Set(prev.map((n) => n.uuid));
        const unique = newNotes.filter((n) => !existingUuids.has(n.uuid));
        return [...prev, ...unique];
      });
    }
    setHasNextPage(data.notes?.pageInfo?.hasNextPage ?? false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const loadMoreInternal = useCallback(() => {
    if (!hasNextPage || loading || !data) return;
    const edges = data.notes?.edges ?? [];
    const cursor = edges[edges.length - 1]?.cursor;
    if (cursor != null) {
      setLastCursor(cursor + 1);
    }
  }, [hasNextPage, loading, data]);

  const loadMoreInternalRef = useRef(loadMoreInternal);
  useEffect(() => {
    loadMoreInternalRef.current = loadMoreInternal;
  }, [loadMoreInternal]);

  const loadMore = useCallback(() => {
    loadMoreInternalRef.current();
  }, []);

  const apolloRefetchRef = useRef(apolloRefetch);
  useEffect(() => {
    apolloRefetchRef.current = apolloRefetch;
  }, [apolloRefetch]);

  const refetch = useCallback(() => {
    setLastCursor(null);
    apolloRefetchRef.current();
  }, []);

  return {
    loading,
    error,
    data,
    notes: allNotes,
    hasNotes: allNotes.length > 0,
    hasNextPage,
    loadMore,
    refetch,
  };
}
